import { Box } from '@mui/material';
import { RefObject, useLayoutEffect, useState } from 'react';

/**
 * The complexity progress bar — a thin strip in the lesson header showing where
 * the user currently sits in the tier's complexity spectrum.
 *
 * It renders as an absolutely positioned child of the header bar, so it spans
 * the full header width regardless of the (much narrower) column the tier
 * selectors live in, and inherits the header's playback slide transform on
 * mobile. Its vertical position is measured from the bottom of the tier-selector
 * row rather than hard-coded, because that row's offset differs between the
 * mobile (80px) and desktop (160px) header layouts.
 */

/** Same green as the tier selector's filled pill (`colors.background.complete`). */
const FILL_COLOR = '#28856F';

/** Thin strip. */
const BAR_HEIGHT_PX = 5;

/** Distance between the bottom of the tier selectors and the top of the bar. */
const GAP_BELOW_TIERS_PX = 6;

/**
 * Behind the Game/Free Play selector (z-index 1000) and the header's own left
 * column (1100), but above the header's gradient background, which is not
 * positioned and so always paints first.
 */
const Z_INDEX = 900;

/**
 * How full the bar is, in [0, 1].
 *
 * Driven by the band *ceiling*: `CR_Max` is the only side of the band that
 * reaches 1.0, and a full bar must reach the chevron area. Switch to
 * `band.crMin` here if the bar should instead read as the floor — the rest of
 * the component is agnostic.
 *
 * Unchanged by the 2026-08-02 move to a centre-anchored, 20%-wide band, and the
 * two landmarks land in the same places as before: the session seeds at 30% and
 * the bar reads 90% when the chevron gate opens. What did change is the middle —
 * the same position now yields a `CR_Max` `cvr/2` higher than it used to, so the
 * bar sits further along for a given difficulty.
 */
function fillFraction(band: { crMin: number; crMax: number }): number {
  return Math.min(Math.max(band.crMax, 0), 1);
}

interface ComplexityProgressBarProps {
  /** Band of the phrase now playing. `null` hides the bar entirely. */
  band: { crMin: number; crMax: number } | null;
  /** The header bar the strip is positioned within. Must be `position: relative`. */
  containerRef: RefObject<HTMLElement | null>;
  /** The tier-selector row the strip sits directly below. */
  anchorRef: RefObject<HTMLElement | null>;
}

const ComplexityProgressBar = ({ band, containerRef, anchorRef }: ComplexityProgressBarProps) => {
  const [top, setTop] = useState<number | null>(null);

  const hasBand = band != null;

  useLayoutEffect(() => {
    if (!hasBand) return;
    const measure = () => {
      const container = containerRef.current;
      const anchor = anchorRef.current;
      if (!container || !anchor) return;
      // Both rects live in the same transformed subtree, so their difference is
      // transform-invariant — the bar stays put relative to the tier selectors
      // while the header slides away during mobile playback.
      const offset =
        anchor.getBoundingClientRect().bottom -
        container.getBoundingClientRect().top +
        GAP_BELOW_TIERS_PX;
      setTop(offset);
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    if (anchorRef.current) observer.observe(anchorRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [hasBand, containerRef, anchorRef]);

  if (band == null || top == null) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: `${top}px`,
        left: 0,
        width: '100%',
        height: `${BAR_HEIGHT_PX}px`,
        zIndex: Z_INDEX,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${fillFraction(band) * 100}%`,
          background: FILL_COLOR,
          borderRadius: `0 ${BAR_HEIGHT_PX / 2}px ${BAR_HEIGHT_PX / 2}px 0`,
          // Bands step once per phrase, so the bar would otherwise jump. Ease
          // out over roughly a beat so it reads as filling and receding.
          transition: 'width 800ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      />
    </Box>
  );
};

export default ComplexityProgressBar;
